import L from 'leaflet';
import geolib from 'geolib';
import Geo from 'leaflet-geometryutil';

const SQFT_CONSTANT = 10.7639;

export const mapCoordinatesArrayToObject = coords => coords.map(coord => ({ lat: coord[1], lng: coord[0] }));

export const cloneObject = (obj, attrs = {}) => Object.assign(Object.create(Object.getPrototypeOf(obj)), obj, attrs);

export const calculateMinXForLatLngs = latLngs => {
  let minX;
  latLngs.forEach(layer => {
    layer.forEach(latLng => {
      if (typeof minX === 'undefined') {
        minX = Math.abs(latLng.lng);
      } else {
        minX = Math.max(Math.abs(latLng.lng), Math.abs(minX));
      }
    });
  });
  return Math.abs(minX);
};

export const calculateMaxXForLatLngs = latLngs => {
  let maxX;
  latLngs.forEach(layer => {
    layer.forEach(latLng => {
      if (typeof maxX === 'undefined') {
        maxX = Math.abs(latLng.lng);
      } else {
        maxX = Math.min(Math.abs(latLng.lng), Math.abs(maxX));
      }
    });
  });
  return Math.abs(maxX);
};

export const calculateMinYForLatLngs = latLngs => {
  let minY;
  latLngs.forEach(layer => {
    layer.forEach(latLng => {
      if (typeof minY === 'undefined') {
        minY = Math.abs(latLng.lat);
      } else {
        minY = Math.max(Math.abs(latLng.lat), Math.abs(minY));
      }
    });
  });
  return Math.abs(minY);
};

export const calculateMaxYForLatLngs = latLngs => {
  let maxY;
  latLngs.forEach(layer => {
    layer.forEach(latLng => {
      if (typeof maxY === 'undefined') {
        maxY = Math.abs(latLng.lat);
      } else {
        maxY = Math.min(Math.abs(latLng.lat), Math.abs(maxY));
      }
    });
  });
  return Math.abs(maxY);
};

/**
 * Function to calculate the aspect ratio of a container using the center coordinate of tha map
 * @param {int} mapCenterY
 */
export const calculateAspectRatio = mapCenterY => Math.cos((parseFloat(mapCenterY) * Math.PI) / 180);

/**
 *
 * @param {int} stageWidth The widith of the stage
 * @param {int} mapWidth The width of the map
 * @param {int} stageHeight The height of the stage
 * @param {int} mapHeight The heigh of the map
 */
export const calculateScale = (stageWidth, mapWidth, stageHeight, mapHeight) =>
  Math.min(stageWidth / mapWidth, stageHeight / mapHeight) / 2;

export const calculateMapDimensions = layers => {
  const coords = layers.map(layer => layer.latLngs);
  const minX = Math.abs(calculateMinXForLatLngs(coords));
  const maxX = Math.abs(calculateMaxXForLatLngs(coords));
  const minY = Math.abs(calculateMinYForLatLngs(coords));
  const maxY = Math.abs(calculateMaxYForLatLngs(coords));

  const mapWidth = minX - maxX;
  const mapHeight = minY - maxY;

  const mapCenterX = (maxX + minX) / 2;
  const mapCenterY = (maxY + minY) / 2;

  const aspectRatio = calculateAspectRatio(mapCenterY);

  return {
    minX,
    maxX,
    minY,
    maxY,
    aspectRatio,
    mapCenterX,
    mapCenterY,
    mapWidth,
    mapHeight,
  };
};
/**
 * Scale coordinates
 * mapDimensions { Object } - Object
 * xy { Object }
 */
export const scaleCoordinates = (mapDimensions, xy, caller) => {
  const { stageWidth, stageHeight, mapWidth, mapHeight, minX, minY } = mapDimensions;
  const { x, y } = xy;

  const xScale = stageWidth / mapWidth;
  const yScale = stageHeight / (mapHeight * 1.15);
  const scale = xScale < yScale ? xScale : yScale;

  const scaledX = (Math.abs(x) - Math.abs(minX)) * scale;
  const scaledY = (Math.abs(y) - Math.abs(minY)) * (scale * 1.15);

  return { x: scaledX, y: scaledY };
};

/**
 * Get scale for coordinates
 * mapDimensions  Object } - Object
 */
export const getCoordinateScale = mapDimensions => {
  const { stageWidth, stageHeight, mapWidth, mapHeight, minX, minY } = mapDimensions;

  const xScale = stageWidth / mapWidth;
  const yScale = stageHeight / (mapHeight * 1.15);
  const scale = xScale < yScale ? xScale : yScale * 2; // TODO:babyjay - the yScale factor doesn't seem to affect it very much (from 1.0 to 2.0)

  return scale;
};

export const calculateMedian = numbers => {
  let median = 0;
  const numsLen = numbers.length;
  numbers.sort();

  if (
    numsLen % 2 ===
    0 // is even
  ) {
    // average of two middle numbers
    median = (numbers[numsLen / 2 - 1] + numbers[numsLen / 2]) / 2;
  } else {
    // is odd
    // middle number only
    median = numbers[(numsLen - 1) / 2];
  }

  return median;
};

export const getPoints = (mapDimensions, latLngs) => {
  // Return flat array lat lngs
  const points = [];
  latLngs.forEach(latLng => {
    const scaled = scaleCoordinates(mapDimensions, {
      x: latLng.lng,
      y: latLng.lat,
    });

    points.push(Math.abs(scaled.x));
    points.push(Math.abs(scaled.y));
  });
  return points;
};
export const getPointLabels = (mapDimensions, latLngs) => {
  return latLngs.map(latLng => {
    const scaled = scaleCoordinates(mapDimensions, {
      x: latLng.lng,
      y: latLng.lat,
    });
    return {
      text: `${parseInt(Math.abs(scaled.x))}, ${parseInt(Math.abs(scaled.y))}`,
      x: Math.abs(scaled.x),
      y: Math.abs(scaled.y),
    };
  });
};

export const getLineLabels = (mapDimensions, latLngs, scale = 1) => {
  // Return flat array lat lngs
  const labels = [];
  latLngs.reduce((prevLatLng, currLatLng, currIndex) => {
    if (!prevLatLng) {
      return currLatLng;
    }
    const scaledCurrent = scaleCoordinates(mapDimensions, {
      x: currLatLng.lng,
      y: currLatLng.lat,
    });
    const scaledPrevious = scaleCoordinates(mapDimensions, {
      x: prevLatLng.lng,
      y: prevLatLng.lat,
    });
    const { x: prevX, y: prevY } = scaledPrevious;
    const { x, y } = scaledCurrent;

    const medianX = calculateMedian([Math.abs(prevX), Math.abs(x)]);
    const medianY = calculateMedian([Math.abs(prevY), Math.abs(y)]);

    // const rotation = 0;
    const rotation =
      prevX < x
        ? Geo.computeAngle({ x: prevX, y: prevY }, { x, y })
        : Geo.computeAngle({ x, y }, { x: prevX, y: prevY });

    const distance = parseFloat(parseFloat(geolib.getDistance(prevLatLng, currLatLng)) / 0.3048).toFixed();

    const labelOffset = {
      x: currLatLng.labelXOffset ? currLatLng.labelXOffset * scale : medianX,
      y: currLatLng.labelYOffset ? currLatLng.labelYOffset * scale : medianY + 10,
    };

    if (distance > 1) {
      labels.push({
        text: `${distance} ft`,
        // labelXOffset,
        // labelYOffset,
        ...labelOffset,
        // rotation,
        lat: currLatLng.lat,
        lng: currLatLng.lng,
        index: currIndex,
        rotation: currLatLng.rotation || rotation,
        scaleX: currLatLng.scaleX,
        scaleY: currLatLng.scaleY,
        hidden: currLatLng.hidden,
      });
    }

    return currLatLng;
  }, latLngs.pop());
  return labels;
};

export const getLabelStartingPosition = layerLatLngs => {
  const center = geolib.getCenter(layerLatLngs);
  const converted = {
    lat: parseFloat(center.latitude),
    lng: parseFloat(center.longitude),
  };

  return converted;
};

export const getShapeLabel = (mapDimensions, latLngs, layer, layerData) => {
  const center = geolib.getCenter(latLngs);
  const scaled = scaleCoordinates(mapDimensions, {
    x: parseFloat(center.longitude),
    y: parseFloat(center.latitude),
  });
  const xScale = mapDimensions.stageWidth / mapDimensions.mapWidth;
  const scaledX = (Math.abs(center.longitude) - Math.abs(mapDimensions.minX)) * xScale;

  let title = '';
  if (layer.layerType === 'baseLayer') {
    title = 'Base Layer';
  } else if (layer.sketchAreaId && layer.sketchSubAreaId) {
    try {
      const sketchArea = layerData.find(l => l.sketchAreaId === layer.sketchAreaId);
      const t1 = sketchArea.name;
      const sketchSubArea = sketchArea.subArea.find(s => s.sketchSubAreaId === layer.sketchSubAreaId);
      const t2 = sketchSubArea.name;
      title = `${t1} / ${t2}`;
    } catch (e) {
      title = layer.id;
    }
  } else if (layer.sketchAreaId) {
    const sketchArea = layerData.find(l => l.sketchAreaId === layer.sketchAreaId);
    title = sketchArea.name;
  }
  //const title = `${layer.sketchArea} / ${layer.sketchSubArea}`;
  const subtitle = layer.sqFt.toString();

  return {
    title: `${title}`,
    subtitle: `${subtitle} ft`,
    titleX: Math.abs(scaled.x) - title.length * 4.5, // eslint-disable-line
    titleY: Math.abs(scaled.y),
    subtitleX: Math.abs(scaled.x) - subtitle.length * 5, // eslint-disable-line
    subtitleY: Math.abs(scaled.y) + 20,
  };
};

// 0.00001 causes too much snapping... 0.000001 has almost no snapping
const SNAP_LAT_DIFF = 0.000001;
const SNAP_LONG_DIFF = 0.000001;

export const snapLatLngs = (latLngs, layers) => {
  // When A leaflet layer is created, snap the points to the base layer
  const newLatLngs = [];
  latLngs.forEach(leafletLatLng => {
    const newLatLng = layers
      .reduce((prevCoords, currentLayer) => prevCoords.concat(currentLayer.latLngs), [])
      .reduce((prev, curr) => {
        const diffLat = Math.abs(prev.lat - curr.lat);
        const diffLng = Math.abs(prev.lng - curr.lng);
        if (diffLat < SNAP_LAT_DIFF && diffLng < SNAP_LONG_DIFF) {
          return curr;
        }
        return prev;
      }, leafletLatLng);
    newLatLngs.push(L.latLng(newLatLng));
  });
  return newLatLngs;
};

export const getSqFt = (latLngs, stories = 1) => {
  return (SQFT_CONSTANT * getArea(latLngs) * stories).toFixed(0);
};

export const getArea = latLngs => {
  return L.GeometryUtil.geodesicArea(latLngs);
};
